import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {errorMessage} from "../../utils/messages";
import CalendarService from "../../services/CalendarService";
import moment from "moment";
import "moment/locale/it";

const PAGE_SIZE = 200;
const INITIAL_PAGES = 3;

export const initialState = {
    startReportMancanti: null,
    endReportMancanti: null,
    loadingReportMancanti: false,
    loadedReportMancanti: false,
    errorReportMancanti: false,
    reportMancanti: [],
    currentApiPage: 0,
    totalApiPages: 0,
    loadingNextApiPage: false,
    activeRequestId: null,
    activeRequestKind: null,
    activePageRequestId: null,
}


const filterAppointmentsWithoutSameDayVisit = (appointments) => {
    return appointments.filter((appointment) => {
        const appointmentDate = new Date(appointment.attributes.start).toISOString().split("T")[0];
        const visits = appointment?.attributes?.patient?.data?.attributes?.visits?.data;
        if (visits === undefined) return false;
        const hasSameDayVisit = visits.some((visit) => {
            const visitDate = visit.attributes.data_visita;
            return appointmentDate === visitDate;
        });
        return !hasSameDayVisit;
    });
};

export const calculateReportMancanti = createAsyncThunk('report-mancanti', async (input, {
    rejectWithValue,
    getState,
    requestId,
}) => {
    console.log("[calculateReportMancanti]")
    try {
        let start, end, effectiveEnd;
        if (input === undefined || input.start === null || input.end === null) {
            start = moment(moment(moment()).startOf('day')).subtract(5, 'days').format("YYYY-MM-DD")
            end = moment(moment(moment()).startOf('day')).format("YYYY-MM-DD")
            effectiveEnd = moment(moment().startOf('day')).add(1, 'days').format("YYYY-MM-DD")
        } else {
            start = input.start
            end = input.end
            effectiveEnd = moment(end).add(1, 'days').format("YYYY-MM-DD")
        }
        const puntivendita = getState().puntiVendita.puntiVendita
        const response = await CalendarService.getReport(puntivendita, start, effectiveEnd);
        const activeReport = getState().reportMancanti;
        if (activeReport && activeReport.activeRequestId !== requestId) return null;
        return {
            data: filterAppointmentsWithoutSameDayVisit(response.data),
            start,
            end
        }
    } catch (err) {
        const activeReport = getState().reportMancanti;
        if (activeReport && activeReport.activeRequestId !== requestId) return null;
        console.error("err", err)
        let message = (
            <div>
                Qualcosa è andato storto nel recuperare i report. Riprovare!
                <br/>
                Se il problema persiste, contattare l' assistenza.
            </div>
        );
        errorMessage(message)
        return rejectWithValue('ERRORE')
    }
})

// Carica le prime INITIAL_PAGES pagine API per la modalità "Mostra tutti"
export const calculateReportMancantiInitial = createAsyncThunk('report-mancanti-initial', async (_, {
    rejectWithValue,
    getState,
    requestId,
}) => {
    console.log("[calculateReportMancantiInitial]")
    try {
        const puntivendita = getState().puntiVendita.puntiVendita
        const start = moment().subtract(100, 'years').format("YYYY-MM-DD")
        const end = moment().format("YYYY-MM-DD")
        const effectiveEnd = moment().add(1, 'days').format("YYYY-MM-DD")

        const firstResponse = await CalendarService.getReportPage(puntivendita, start, effectiveEnd, 1, PAGE_SIZE);
        if (getState().reportMancanti.activeRequestId !== requestId) return null;
        const totalPages = firstResponse.meta?.pagination?.pageCount ?? 1;
        const pagesToLoad = Math.min(INITIAL_PAGES, totalPages);

        // Collect all records from all initial pages before rendering
        const allRecords = [...filterAppointmentsWithoutSameDayVisit(firstResponse.data)];

        for (let page = 2; page <= pagesToLoad; page++) {
            const resp = await CalendarService.getReportPage(puntivendita, start, effectiveEnd, page, PAGE_SIZE, false);
            if (getState().reportMancanti.activeRequestId !== requestId) return null;
            allRecords.push(...filterAppointmentsWithoutSameDayVisit(resp.data));
        }

        return { start, end, data: allRecords, currentApiPage: pagesToLoad, totalApiPages: totalPages }
    } catch (err) {
        if (getState().reportMancanti.activeRequestId !== requestId) return null;
        console.error("err", err)
        let message = (
            <div>
                Qualcosa è andato storto nel recuperare i report. Riprovare!
                <br/>
                Se il problema persiste, contattare l' assistenza.
            </div>
        );
        errorMessage(message)
        return rejectWithValue('ERRORE')
    }
}, {
    condition: (_, {getState}) => {
        const report = getState().reportMancanti;
        return !(report?.loadingReportMancanti && report.activeRequestKind === 'all');
    },
})

// Carica la pagina API successiva on demand (chiamato mentre l'utente naviga la tabella)
export const loadNextApiPage = createAsyncThunk('report-mancanti-load-next', async (_, {
    rejectWithValue,
    getState,
    dispatch,
    requestId,
}) => {
    const PAGES_PER_LOAD = 3;
    const { currentApiPage, totalApiPages } = getState().reportMancanti;
    if (currentApiPage >= totalApiPages) return null;

    try {
        const puntivendita = getState().puntiVendita.puntiVendita;
        const start = moment().subtract(100, 'years').format("YYYY-MM-DD");
        const effectiveEnd = moment().add(1, 'days').format("YYYY-MM-DD");

        let loadedUpTo = currentApiPage;
        for (let i = 1; i <= PAGES_PER_LOAD; i++) {
            const nextPage = currentApiPage + i;
            if (nextPage > totalApiPages) break;
            const response = await CalendarService.getReportPage(puntivendita, start, effectiveEnd, nextPage, PAGE_SIZE, false);
            if (getState().reportMancanti.activePageRequestId !== requestId) return null;
            const filtered = filterAppointmentsWithoutSameDayVisit(response.data);
            dispatch(ReportMacantiSlice.actions.appendReportMancanti({data: filtered, page: nextPage, requestId}));
            loadedUpTo = nextPage;
        }

        return { currentApiPage: loadedUpTo }
    } catch (err) {
        console.error("err", err)
        return rejectWithValue('ERRORE')
    }
}, {
    condition: (_, {getState}) => {
        const report = getState().reportMancanti;
        return Boolean(report && report.activeRequestKind === 'all' && !report.loadingReportMancanti
            && !report.loadingNextApiPage && report.currentApiPage < report.totalApiPages);
    },
})

export const ReportMacantiSlice = createSlice({
    name: 'reportMancanti',
    initialState,
    reducers: {
        resetState: () => initialState,
        appendReportMancanti: (state, action) => {
            if (state.activePageRequestId !== action.payload.requestId) return;
            state.reportMancanti.push(...action.payload.data);
            // Commit each completed page, so retrying after a later failure
            // cannot append the already completed page again.
            state.currentApiPage = action.payload.page;
        }
    },
    extraReducers: (builder) => {
        builder
            // Modalità normale (range picker)
            .addCase(calculateReportMancanti.pending, (state, action) => {
                state.activeRequestId = action.meta.requestId;
                state.activeRequestKind = 'range';
                state.activePageRequestId = null;
                state.loadingNextApiPage = false;
                state.loadingReportMancanti = true
                state.loadedReportMancanti = false
                state.errorReportMancanti = false
                state.currentApiPage = 0
                state.totalApiPages = 0
            })
            .addCase(calculateReportMancanti.fulfilled, (state, action) => {
                if (state.activeRequestId !== action.meta.requestId) return;
                state.loadingReportMancanti = false
                state.reportMancanti = action.payload.data
                state.startReportMancanti = action.payload.start
                state.endReportMancanti = action.payload.end
                state.loadedReportMancanti = true
                state.errorReportMancanti = false
            })
            .addCase(calculateReportMancanti.rejected, (state, action) => {
                if (state.activeRequestId !== action.meta.requestId) return;
                state.loadingReportMancanti = false
                state.reportMancanti = []
                state.loadedReportMancanti = true
                state.errorReportMancanti = true
            })
            // Modalità "Mostra tutti" — caricamento iniziale (prime 3 pagine)
            .addCase(calculateReportMancantiInitial.pending, (state, action) => {
                state.activeRequestId = action.meta.requestId;
                state.activeRequestKind = 'all';
                state.activePageRequestId = null;
                state.loadingReportMancanti = true
                state.loadedReportMancanti = false
                state.errorReportMancanti = false
                state.reportMancanti = []
                state.currentApiPage = 0
                state.totalApiPages = 0
                state.loadingNextApiPage = false
            })
            .addCase(calculateReportMancantiInitial.fulfilled, (state, action) => {
                if (state.activeRequestId !== action.meta.requestId || !action.payload) return;
                state.loadingReportMancanti = false
                state.reportMancanti = action.payload.data
                state.startReportMancanti = action.payload.start
                state.endReportMancanti = action.payload.end
                state.currentApiPage = action.payload.currentApiPage
                state.totalApiPages = action.payload.totalApiPages
                state.loadedReportMancanti = true
                state.errorReportMancanti = false
            })
            .addCase(calculateReportMancantiInitial.rejected, (state, action) => {
                if (state.activeRequestId !== action.meta.requestId) return;
                state.loadingReportMancanti = false
                state.reportMancanti = []
                state.loadedReportMancanti = true
                state.errorReportMancanti = true
            })
            // Caricamento pagina successiva on demand
            .addCase(loadNextApiPage.pending, (state, action) => {
                state.activePageRequestId = action.meta.requestId;
                state.loadingNextApiPage = true
            })
            .addCase(loadNextApiPage.fulfilled, (state, action) => {
                if (state.activePageRequestId !== action.meta.requestId) return;
                state.loadingNextApiPage = false
                if (action.payload) {
                    state.currentApiPage = action.payload.currentApiPage
                }
            })
            .addCase(loadNextApiPage.rejected, (state, action) => {
                if (state.activePageRequestId !== action.meta.requestId) return;
                state.loadingNextApiPage = false
            })
    }
})


export default ReportMacantiSlice.reducer;
